import { ActionContext } from 'vuex';
/* eslint-disable no-param-reassign */
import crypto from 'crypto';
import jwtDecode from 'jwt-decode';
import store, { tokensUpdateChannel } from '@/store/index';
import config from '../config/ConfigProvider';
import authApi from '../data-provider/authentication';
import { AuthState, Rootstate } from './types';

const basePath = config.value('basePath');
const basePathWithoutLeadingSlash = basePath.substr(1);

export const initialState: AuthState = {
  codeVerifier: '',
  accessToken: '',
  refreshToken: '',
  idToken: '',
  reauthenticate: '',
  inProgress: false,
};

const authentication = {
  namespaced: true,

  state: initialState,

  getters: {
    getCodeVerifier: (state: AuthState) => state.codeVerifier,

    getAccessToken: (state: AuthState) => state.accessToken,

    getIdToken: (state: AuthState) => state.idToken,

    getRefreshToken: (state: AuthState) => state.refreshToken,

    getLoginParams: (state: AuthState) => {
      const codeChallenge = crypto
        .createHash('sha256')
        .update(state.codeVerifier)
        .digest()
        .toString('base64')
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');

      const params = new URLSearchParams();
      const nonce = crypto.randomBytes(20).toString('hex');

      params.append('response_type', 'code');
      params.append('scope', ['openid', 'profile', 'email'].join(' '));
      params.append('client_id', config.value('authClientId'));
      params.append('redirect_uri', config.value('authRedirectUrl') + basePathWithoutLeadingSlash);
      params.append('code_challenge', codeChallenge);
      params.append('code_challenge_method', 'S256');
      params.append('amr_hint', config.value('authAmrHint'));
      params.append('nonce', nonce);
      localStorage.setItem('nonce', nonce);

      return params;
    },

    loggedIn: (state: AuthState) => {
      // TODO can we cache this and not have to decode it every time??
      const token = jwtDecode(state.accessToken) as any;
      const scope = token.scope as Array<string>;
      return state.accessToken !== '' && scope.includes('ilt.content.write');
    },
  },

  mutations: {
    changeCodeVerifier: (state: AuthState, codeVerifier: AuthState['codeVerifier']) => {
      state.codeVerifier = codeVerifier;
    },

    changeReauthenticate(state: AuthState, reauthenticate: AuthState['reauthenticate']) {
      state.reauthenticate = reauthenticate;
    },

    changeAccessToken(state: AuthState, accessToken: AuthState['accessToken']) {
      state.accessToken = accessToken;
    },

    changeRefreshToken(state: AuthState, refreshToken: AuthState['refreshToken']) {
      state.refreshToken = refreshToken;
    },

    changeIdToken(state: AuthState, idToken: AuthState['idToken']) {
      state.idToken = idToken;
    },

    setInProgress(state: AuthState, status: AuthState['inProgress']) {
      state.inProgress = status;
    },
  },

  actions: {
    changeCodeVerifier({ commit }: ActionContext<AuthState, Rootstate>) {
      const verifier = crypto
        .randomBytes(32)
        .toString('base64')
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');

      commit('changeCodeVerifier', verifier);
      commit('setInProgress', false);
    },

    clearTokens({ commit }: ActionContext<AuthState, Rootstate>, logout = true) {
      commit('changeAccessToken', '');
      commit('changeRefreshToken', '');

      tokensUpdateChannel.postMessage({
        accessToken: null,
        refreshToken: null,
        idToken: null,
      });

      if (logout) {
        window.location.href = basePath || '/';
      }
    },

    setSuppliedAccessToken({ commit }: ActionContext<AuthState, Rootstate>, accessToken: string) {
      commit('changeAccessToken', accessToken);
    },

    setReauthenticate({ commit }: ActionContext<AuthState, Rootstate>, reauthenticate: string) {
      commit('changeReauthenticate', reauthenticate);
    },

    authenticateWithCode({ state, commit, dispatch }: ActionContext<AuthState, Rootstate>, code: string) {
      commit('setInProgress', true);

      const tokenParams = new URLSearchParams();

      tokenParams.append('scope', 'openid');
      tokenParams.append('redirect_uri', config.value('authRedirectUrl') + basePathWithoutLeadingSlash);
      tokenParams.append('client_id', config.value('authClientId'));
      tokenParams.append('code', code);
      tokenParams.append('grant_type', 'authorization_code');
      tokenParams.append('code_verifier', state.codeVerifier);

      return authApi
        .getToken(tokenParams)
        .then((response: any) => {
          if (response.status !== 200) {
            commit('setInProgress', false);
            throw new Error(`Failed to Authenticate with code due to an error - "${response.error}".`);
          }

          const accessToken = response.data.access_token;
          const refreshToken = response.data.refresh_token;
          const idToken = response.data.id_token;
          const { nonce, email, name } = jwtDecode(idToken) as {
            nonce: string;
            email: string;
            name: string;
          };
          const { roles } = jwtDecode(accessToken) as { roles: Array<string> };
          if (nonce !== localStorage.getItem('nonce')) {
            throw new Error("Failed to Authenticate: 'nonce' field is invalid.");
          }

          commit('user/setUserName', name, { root: true });
          commit('user/setUserEmail', email, { root: true });
          // TODO change 'roles' with 'scope' (separated by space) after Viktor's release
          commit('user/setUserRoles', roles, { root: true });
          commit('changeAccessToken', accessToken);
          commit('changeRefreshToken', refreshToken);
          commit('changeIdToken', idToken);
          commit('setInProgress', false);

          tokensUpdateChannel.postMessage({
            accessToken,
            refreshToken,
            idToken
          });
        })
        .catch((error) => {
          dispatch('clearTokens', false);
          throw new Error(`Failed to Authenticate: ${error.message}`);
        });
    },

    authenticateWithRefreshToken(
      { state, commit }: ActionContext<AuthState, Rootstate>,
      cb: ((value: void) => void | PromiseLike<void>) | null | undefined,
    ) {
      commit('setInProgress', true);

      const refreshParams = new URLSearchParams();

      refreshParams.append('scope', 'openid');
      refreshParams.append('client_id', config.value('authClientId'));
      refreshParams.append('grant_type', 'refresh_token');
      refreshParams.append('refresh_token', state.refreshToken);

      return authApi
        .refreshToken(refreshParams)
        .then((response) => {
          const accessToken = response.data.access_token;
          const refreshToken = response.data.refresh_token;
          const idToken = response.data.id_token;
          commit('changeAccessToken', accessToken);
          commit('changeRefreshToken', refreshToken);
          commit('changeIdToken', idToken);
          commit('setInProgress', false);

          tokensUpdateChannel.postMessage({
            accessToken,
            refreshToken,
            idToken
          });
        })
        .then(cb)
        .catch((error) => {
          console.error(error);

          store.dispatch('authentication/clearTokens', true);
        })
        .finally(() => {
          commit('setInProgress', false);
        });
    },

    setInProgress({ commit }: ActionContext<AuthState, Rootstate>, status = true) {
      commit('setInProgress', status);
    },
  },
};
export default authentication;
